import {Action, action, createStore, State, Store, Thunk, thunk, useStoreActions, useStoreState} from 'easy-peasy';
import Message, {ROLE} from '../models/message.model';
import {handleApiResponse, initChat, logJsError} from './api.service';
import {logger} from './utils.service';

let isInitChatInProgress = false;

export class StorePath {
	static AGENT_TYPING = 'agent_typing';
	static CONVERSION_RECORDED = 'conversion_recorded';
	static DEALER_INFO = 'dealer_info';
	static IS_SCHEDULER_ACTIVE = 'is_scheduler_active';
	static MESSAGES = 'messages';
	static PICKUP_LINE = 'pickup_line';
	static SETTINGS = 'settings';
	static TRACK_DATA = 'track_data';
	static TRACK_PARAMS = 'track_params';
}

export interface StoreKeyValue {
	key: string,
	value: unknown,
}

export interface StoreModel {
	data: Record<string, unknown>;
	setValue: Action<Record<string, unknown>, StoreKeyValue>;
	initChat: Thunk<StoreModel>;
}

type StoreAction = (payload: { key: string, value: unknown }) => void;

const createStoreModel = (): StoreModel => ({
	data: {
		// Add below all the default values if you need a default value
		messages: [],
		track_params: {},
		track_data: {},
		dealer_info: {
			name: '',
			oems: [''],
		},
		agent_typing: false,
		pickup_line: null,
		conversion_recorded: false,
		is_scheduler_active: false,
	},
	setValue: action((state: State<StoreModel>, payload: StoreKeyValue): void => {
		state.data[payload.key] = payload.value;
	}),
	initChat: thunk(async (action): Promise<void> => {
		if (isInitChatInProgress) {
			logger('initChat already in progress. Skipping duplicate call.');
			return;
		}
		try {
			isInitChatInProgress = true;
			action.setValue({key: StorePath.AGENT_TYPING, value: true});
			const response = await initChat();
			if (!response) {
				isInitChatInProgress = false;
				return;
			}
			const data = await handleApiResponse(response);

			if (data?.messages?.length) {
				const message: Message = data.messages[0].message;
				message.role = ROLE.ASSISTANT;
				action.setValue({key: StorePath.MESSAGES, value: [message]});
			} else {
				logJsError({name: 'initChat()', message: 'No messages received. Response URL: ' + response.url});
			}
		} catch (error: any) {
			logJsError(error);
		} finally {
			isInitChatInProgress = false;
			action.setValue({key: StorePath.AGENT_TYPING, value: false});
		}
	}),
});

const store: Store<StoreModel> = createStore(createStoreModel());

export default store;

export const getFromStore = (key: string) => useStoreState((state: State) => (state as StoreModel).data[key]);
export const useStoreActionsFromStore = useStoreActions;
export const storeSaveAction = (actions: any) => actions.setValue;
export const storeInitChatAction = (actions: any) => actions.initChat;

export const saveToStore = (storeAction: unknown, key: string, value: unknown) => {
	if (typeof value === 'object' && Array.isArray(value)) {
		value = Array.from(value);
	}
	(storeAction as StoreAction)({key, value});
};
